import React, { useState } from 'react';
import { T, Var } from 'gt-react';
import { useDocumentViewerUrl } from '@/api/rpc/hooks/core/useDocumentViewerUrl';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { FEATURE_FLAGS } from '@erp/shared-types';
import LoadingSpinner from './LoadingSpinner';
import { scaledIframeStyle } from './officeViewerScale';

/** File extensions supported by the Microsoft Office Online viewer */
const OFFICE_VIEWER_EXTENSIONS = new Set([
  'xlsx',
  'xls',
  'docx',
  'doc',
  'pptx',
  'ppt',
]);

/**
 * Check if a file name has an extension supported by Office Online viewer.
 */
export function isOfficeViewerSupported(fileName: string): boolean {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return OFFICE_VIEWER_EXTENSIONS.has(ext);
}

/**
 * Hook to check whether the Office Online viewer feature is enabled.
 */
export function useIsOfficeViewerEnabled(): boolean {
  const { enabled } = useFeatureFlag(
    FEATURE_FLAGS['msft-office-viewer-enabled'],
    false,
  );
  return enabled;
}

/** Box shown when the document cannot be rendered by Office Online. */
function UnsupportedNotice({
  fileName,
  className,
  style,
}: {
  fileName: string | undefined;
  className: string | undefined;
  style: React.CSSProperties;
}) {
  return (
    <div
      className={`flex items-center justify-center bg-gray-50 border border-gray-200 rounded text-gray-600 text-sm p-4 ${className ?? ''}`}
      style={style}
    >
      {fileName ? (
        <T context="error when file type is not supported for online viewing">
          File type not supported for online viewing: <Var>{fileName}</Var>
        </T>
      ) : (
        <T>Document not available for online viewing</T>
      )}
    </div>
  );
}

interface OfficeDocViewerProps {
  /** The document ID to display */
  documentId: string;
  /** Width of the viewer container. Defaults to '100%'. */
  width?: string | number;
  /** Height of the viewer container. Defaults to '600px'. */
  height?: string | number;
  /** Optional CSS class name for the container */
  className?: string;
  /**
   * Visual scale (1 = native). The iframe is CSS-scaled from its top-left
   * corner and sized up by the inverse so the document still fills the box
   * — Office Online exposes no zoom API to the embedding page.
   */
  zoom?: number;
}

/**
 * Renders Microsoft Office documents (Excel, Word, PowerPoint) using
 * the free Office Online embed viewer via an iframe.
 *
 * Files are fetched from S3 using a time-limited pre-signed URL,
 * which is passed to Microsoft's viewer endpoint.
 *
 * Gated by the `msft-office-viewer-enabled` feature flag — renders
 * nothing when the flag is off.
 */
export const OfficeDocViewer: React.FC<OfficeDocViewerProps> = ({
  documentId,
  width = '100%',
  height = '600px',
  className,
  zoom = 1,
}) => {
  const isEnabled = useIsOfficeViewerEnabled();
  const { data, isLoading, error } = useDocumentViewerUrl({
    documentId: isEnabled ? documentId : undefined,
  });
  const [iframeLoaded, setIframeLoaded] = useState(false);

  if (!isEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <div
        className={`flex items-center justify-center ${className ?? ''}`}
        style={{ width, height }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-red-50 border border-red-200 rounded text-red-700 text-sm p-4 ${className ?? ''}`}
        style={{ width, height }}
      >
        <T context="error message when document viewer fails to load">
          Failed to load document viewer: <Var>{error.message}</Var>
        </T>
      </div>
    );
  }

  if (!data || !data.supported) {
    return (
      <UnsupportedNotice
        fileName={data?.fileName}
        className={className}
        style={{ width, height }}
      />
    );
  }

  return (
    <div
      id="msdoc-renderer"
      className={className}
      style={{ width, height, overflow: 'hidden', position: 'relative' }}
    >
      {!iframeLoaded && (
        <div className="flex items-center justify-center absolute inset-0 bg-background/80 z-10">
          <LoadingSpinner />
        </div>
      )}
      <iframe
        id="msdoc-iframe"
        title={`Office viewer: ${data.fileName}`}
        src={data.viewerUrl}
        frameBorder={0}
        onLoad={() => setIframeLoaded(true)}
        style={scaledIframeStyle(zoom)}
      />
    </div>
  );
};
